import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import classNames from 'classnames';
import DashCardHeading from '../DashCardHeading';
import FileActivity from 'router/subrouters/DataRoom/subrouters/Reports/pages/Overview/components/FileActivity';
import { useTranslation } from 'react-i18next';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { DataRoomDocumentsStats } from 'router/subrouters/DataRoom/models/DataRoom';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import utils from 'utils';
import DashEmptyState from '../DashEmptyState';
import DataRoomUploadContext from 'router/subrouters/DataRoom/providers/DataRoomUpload/DataRoomUpload.context';
import { StandardEventTypes } from 'modules/bus';
import useBusEvent from 'hooks/useBusEvent';
import DropzoneActiveOverlay from 'router/subrouters/DataRoom/components/DropzoneActiveOverlay';
import { Button } from 'ncoded-component-library';
import EmptyStateImage from 'assets/images/reports-empty-state.webp';

import './DashDocuments.styles.scss';

type DashDocumentsProps = {
  className?: string;
};

const DashDocuments: React.FC<DashDocumentsProps> = (props) => {
  const { className } = props;

  const [documentsStats, setDocumentsStats] =
    useState<DataRoomDocumentsStats>();
  const [areDocumentsStatsLoading, setAreDocumentsStatsLoading] =
    useState(true);

  const { hasAtLeastOneFileInDR } = documentsStats || {};

  const {
    dataRoom: { _id: dataRoomId },
    refetch,
  } = useContext(DataRoomContext);
  const { isDragActive, dropzoneContainerProps, onUploadClick } = useContext(
    DataRoomUploadContext,
  );
  const { popServerError } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const isNotEmpty = areDocumentsStatsLoading || hasAtLeastOneFileInDR;

  const classes = classNames(
    'dash-documents',
    'card',
    'white-panel',
    className,
    {
      'stat-card': isNotEmpty,
    },
  );

  const { type, count } = utils.timeSpentInSecondsHumanize(
    documentsStats?.totalNumberOfSecondsSpent || 0,
  );

  const renderTile = useCallback(
    (stat: ReactNode, label: string) => (
      <div>
        {!areDocumentsStatsLoading && (
          <>
            <p>{stat}</p>
            <p>{label}</p>
          </>
        )}
      </div>
    ),
    [areDocumentsStatsLoading],
  );

  useEffect(() => {
    (async () => {
      try {
        setAreDocumentsStatsLoading(true);
        const { data } = await dataRoomApi.room.getDocumentsStats(dataRoomId);
        setDocumentsStats(data);
      } catch (e) {
        popServerError(e);
      } finally {
        setAreDocumentsStatsLoading(false);
      }
    })();
  }, [dataRoomId, popServerError]);

  const newNodeAdded = useCallback(
    () =>
      setDocumentsStats((prev) => ({ ...prev, hasAtLeastOneFileInDR: true })),
    [],
  );

  useBusEvent(StandardEventTypes.DATA_ROOM_NODE_ADDED, newNodeAdded);

  return (
    <div
      className={classes}
      {...(!isNotEmpty && {
        ...dropzoneContainerProps,
        onClick: onUploadClick,
        style: { cursor: 'pointer' },
      })}
    >
      {isNotEmpty ? (
        <>
          <DashCardHeading
            heading={t('insights')}
            linkLabel={t('analytics')}
            linkTo="../reports"
          />
          <div className="stat-tiles">
            {renderTile(
              documentsStats?.totalNumberOfViews,
              t('views', { count: documentsStats?.totalNumberOfViews }),
            )}
            {renderTile(
              count === 0
                ? `0m`
                : t(`timeSpentInSecondsHumanizeShort.${type}`, { count }),
              t('totalTime'),
            )}
            {renderTile(
              documentsStats?.totalNumberOfDownloads,
              t('downloads', {
                count: documentsStats?.totalNumberOfDownloads,
              }),
            )}
          </div>
          <FileActivity
            className="card-wrapper"
            withManageAccessCell
            updateNodes={refetch}
          />
        </>
      ) : (
        <>
          <DropzoneActiveOverlay isVisible={isDragActive} />
          <DashEmptyState
            title={t(hasAtLeastOneFileInDR ? 'fileInsights' : 'addDocuments')}
            {...(hasAtLeastOneFileInDR && {
              imageSrc: EmptyStateImage,
              imageDescription: t('filesHaventBeenViewedYet'),
            })}
          >
            <Button variant="outline">
              {t(
                hasAtLeastOneFileInDR
                  ? 'addMoreDocuments'
                  : 'startAddingDocuments',
              )}
            </Button>
          </DashEmptyState>
        </>
      )}
    </div>
  );
};

export default DashDocuments;
